import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TuiAccordion, TUI_CONFIRM, TuiInputDate } from '@taiga-ui/kit';
import {
  TuiDialogService,
  TuiError,
  TuiButton,
  TuiInput,
  TuiTextfield,
  TuiCalendar,
} from '@taiga-ui/core';
import { HyphenedStringTuiDateTransformerDirective } from '@core/directives/hyphenedStringTuiDateTransformer.directive';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import {
  EMPTY,
  filter,
  merge,
  Observable,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tuiMarkControlAsTouchedAndValidate } from '@taiga-ui/cdk';
import { XAlertService } from '@core/services/x-alert.service';
import { RequestService } from '@core/services/request/request.service';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  RequestFormCommonSupplierActualDeliveryBlockTranslations,
  RequestFormCommonSupplierActualDeliveryBlockTranslationsPayload,
} from '@core/types';

enum FormFields {
  ActualReleaseDate = 'actualReleaseDate',
}

@Component({
  selector: 'x-request-form-common-supplier-actual-release-date',
  imports: [
    TuiTextfield,
    TuiCalendar,
    AsyncPipe,
    ReactiveFormsModule,
    TuiInput,
    TuiInputDate,
    HyphenedStringTuiDateTransformerDirective,
    TuiAccordion,
    TuiError,
    TuiButton,
    TranslatePipe,
  ],
  templateUrl:
    './request-form-common-supplier-actual-release-date.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormCommonSupplierActualReleaseDateComponent implements OnInit {
  private readonly requestService = inject(RequestService);
  private readonly requestApiService = inject(RequestsApiService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly xAlertService = inject(XAlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);

  private translates: RequestFormCommonSupplierActualDeliveryBlockTranslationsPayload =
    {
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.DialogChangeReleaseDate]:
        'Вы уверены, что хотите изменить дату выдачи',
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.DialogChangeReleaseDateYes]:
        'Да, сохранить',
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.DialogChangeReleaseDateNo]:
        'Отмена',
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.SuccessChangeReleaseDate]:
        'Фактическая дата выдачи сохранена',
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.DialogResetReleaseDate]:
        'Вы действительно хотите сбросить фактическую дату выдачи?',
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.DialogResetReleaseDateYes]:
        'Да, сбросить',
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.DialogResetReleaseDateNo]:
        'Отмена',
      [RequestFormCommonSupplierActualDeliveryBlockTranslations.SuccessResetReleaseDate]:
        'Фактическая дата выдачи сброшена',
    };

  private readonly locale = inject(LOCALE_ID);
  private readonly datePipe = new DatePipe(this.locale);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly $request = this.requestService.$request;
  FormFields = FormFields;

  readonly formGroup = new FormGroup({
    [FormFields.ActualReleaseDate]: new FormControl<string | null>(null),
  });

  setActualReleaseDate$ = new Subject<void>();

  changeActualReleaseDate$ = new Subject<void>();

  get actualReleaseDateField() {
    return this.formGroup.controls[FormFields.ActualReleaseDate];
  }

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate(
        Object.values(RequestFormCommonSupplierActualDeliveryBlockTranslations),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));

    this.formGroup.patchValue(this.requestService.$request());

    merge(
      this.setActualReleaseDate$.pipe(
        switchMap(() => this.performSetActualReleaseDate$()),
      ),
      this.changeActualReleaseDate$.pipe(
        switchMap(() => this.performChangeActualReleaseDate$()),
      ),
    )
      .pipe(
        tap(() => {
          this.actualReleaseDateField.removeValidators(Validators.required);
          this.requestService.reloadRequest();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  canUserEditActualReleaseDate$(): Observable<boolean> {
    return this.permissionService.canEditRequestRatings$;
  }

  performSetActualReleaseDate$() {
    this.actualReleaseDateField.addValidators(Validators.required);
    tuiMarkControlAsTouchedAndValidate(this.formGroup);
    if (this.actualReleaseDateField.valid) {
      return this.dialogService$
        .open<boolean>(TUI_CONFIRM, {
          label: `${this.translates[RequestFormCommonSupplierActualDeliveryBlockTranslations.DialogChangeReleaseDate]} ${this.datePipe.transform(
            this.actualReleaseDateField.value,
            'dd MMMM yyyy',
          )}?`,
          size: 's',
          closable: false,
          data: {
            yes: this.translates[
              RequestFormCommonSupplierActualDeliveryBlockTranslations
                .DialogChangeReleaseDateYes
            ],
            no: this.translates[
              RequestFormCommonSupplierActualDeliveryBlockTranslations
                .DialogChangeReleaseDateNo
            ],
          },
        })
        .pipe(
          filter(Boolean),
          switchMap(() =>
            this.requestApiService.setActualReleaseDate$(
              this.$request().id,
              this.actualReleaseDateField.value as string,
            ),
          ),
          tap(() => {
            this.xAlertService.showMessage(
              this.translates[
                RequestFormCommonSupplierActualDeliveryBlockTranslations
                  .SuccessChangeReleaseDate
              ],
              {
                appearance: 'positive',
              },
            );
          }),
        );
    }

    return EMPTY;
  }

  performChangeActualReleaseDate$() {
    if (this.actualReleaseDateField.value) {
      return this.performSetActualReleaseDate$();
    } else {
      return this.dialogService$
        .open<boolean>(TUI_CONFIRM, {
          label:
            this.translates[
              RequestFormCommonSupplierActualDeliveryBlockTranslations
                .DialogResetReleaseDate
            ],
          size: 's',
          closable: false,
          data: {
            yes: this.translates[
              RequestFormCommonSupplierActualDeliveryBlockTranslations
                .DialogResetReleaseDateYes
            ],
            no: this.translates[
              RequestFormCommonSupplierActualDeliveryBlockTranslations
                .DialogResetReleaseDateNo
            ],
          },
        })
        .pipe(
          filter(Boolean),
          switchMap(() =>
            this.requestApiService.resetActualReleaseDate$(this.$request().id),
          ),
          tap(() => {
            this.xAlertService.showMessage(
              this.translates[
                RequestFormCommonSupplierActualDeliveryBlockTranslations
                  .SuccessResetReleaseDate
              ],
              {
                appearance: 'positive',
              },
            );
          }),
        );
    }
  }
}
